import type { BufferGeometry } from 'three';
import type { Ring, Vec2 } from '../../../../geo/types';
import type { LinearRGB } from '../../../../core/color';
import type { PaletteModel } from '../../../../data/types';
import type { Rng } from '../../../../core/rng';
import { createMeshBuilder, type Frame, type VertexStyle } from '../../../mesh/meshBuilder';
import { EDGE_ID, SURFACE } from '../../../mesh/attributes';
import { prismSolid } from '../../solids';
import { WINDOW } from '../masonry';
import { TRACERY, TRACERY_KINDS, type TraceryKind } from './traceryLayout';

/**
 * Unit templates of the tracery, extruded from their outlines a unit out from the glass: a mullion one
 * unit wide and tall about its axis; a light's head for a light one unit wide, springing at y = 0;
 * the fork for a window one unit wide. The rib under an arch keeps to its light, so the heads of two
 * lights meet on the mullion between them and the jamb-side feet stand on the jambs.
 */
const SEGMENTS = { head: 4, arm: 5 } as const;

const SIXTY = Math.PI / 3;
const IDENTITY: Frame = { origin: [0, 0, 0], yaw: 0 };

/**
 * The windows' stone, Lede stone, pale grey-yellow, for jambs and tracery (Janse 2004, sections 1.6
 * and 1.9): the palette's light strokes, which the painting's window frames also take.
 */
export function dressingStone(palette: PaletteModel): LinearRGB {
  return palette.highlightWhite.median.linear;
}

function arc(centre: Vec2, radius: number, from: number, to: number, segments: number): Vec2[] {
  return Array.from({ length: segments + 1 }, (_, k): Vec2 => {
    const a = from + ((to - from) * k) / segments;
    return [centre[0] + radius * Math.cos(a), centre[1] + radius * Math.sin(a)];
  });
}

/** A unit light's head: the rib between its equilateral arch and the arch a rib's width inside it. */
export function lightHeadOutline(): Ring {
  const n = SEGMENTS.head;
  const inner = 1 - TRACERY.lightRib;
  const apex = Math.atan2(Math.sqrt(inner * inner - 0.25), -0.5);
  return [
    ...arc([1, 0], inner, Math.PI, apex, n),
    ...arc([0, 0], inner, Math.PI - apex, 0, n).slice(1),
    ...arc([0, 0], 1, 0, SIXTY, n),
    ...arc([1, 0], 1, Math.PI - SIXTY, Math.PI, n).slice(1),
  ];
}

/* The left half of the unit window's arch as the walls cut it (panels.pointedWindow), springing to apex. */
function archLeft(): Vec2[] {
  const n = WINDOW.archSegments;
  return Array.from({ length: n + 1 }, (_, k): Vec2 => [1 - Math.cos((SIXTY * k) / n), Math.sin((SIXTY * k) / n)]);
}

interface Hit {
  readonly angle: number;
  /** Position along the arch polyline: segment index plus the fraction along it. */
  readonly along: number;
}

/* Where the circle about `centre` first meets the arch, going up from angle 0. */
function firstHit(centre: Vec2, radius: number, arch: readonly Vec2[]): Hit {
  const hits = arch.slice(1).flatMap((b, i): Hit[] => {
    const a = arch[i]!;
    const d: Vec2 = [b[0] - a[0], b[1] - a[1]];
    const f: Vec2 = [a[0] - centre[0], a[1] - centre[1]];
    const qa = d[0] * d[0] + d[1] * d[1];
    const qb = 2 * (f[0] * d[0] + f[1] * d[1]);
    const qc = f[0] * f[0] + f[1] * f[1] - radius * radius;
    const disc = qb * qb - 4 * qa * qc;
    if (disc < 0) return [];
    return [(-qb - Math.sqrt(disc)) / (2 * qa), (-qb + Math.sqrt(disc)) / (2 * qa)]
      .filter((s) => s >= 0 && s <= 1)
      .map((s) => ({ angle: Math.atan2(f[1] + s * d[1], f[0] + s * d[0]), along: i + s }));
  });
  return hits.filter((h) => h.angle > 0).reduce((a, h) => (h.angle < a.angle ? h : a));
}

function pointAlong(arch: readonly Vec2[], along: number): Vec2 {
  const i = Math.min(arch.length - 2, Math.floor(along));
  const [a, b] = [arch[i]!, arch[i + 1]!];
  const s = along - i;
  return [a[0] + s * (b[0] - a[0]), a[1] + s * (b[1] - a[1])];
}

/* The left arm of the fork, drawn about its centre on the springing line a window's width left of the post. */
function leftArm(): Vec2[] {
  const centre: Vec2 = [-0.5, 0];
  const half = TRACERY.forkRib / 2;
  const [innerR, outerR] = [1 - half, 1 + half];
  const arch = archLeft();
  const inner = firstHit(centre, innerR, arch);
  const outer = firstHit(centre, outerR, arch);
  const crotch = Math.atan2(Math.sqrt(outerR * outerR - 1), 1);
  const between = arch.filter((_, k) => k > inner.along && k < outer.along).reverse();
  return [
    ...arc(centre, outerR, crotch, outer.angle, SEGMENTS.arm),
    ...between,
    pointAlong(arch, inner.along),
    ...arc(centre, innerR, inner.angle, 0, SEGMENTS.arm).slice(1),
  ];
}

/** The unit window's fork: the middle post's two arms of the window arch's radius, from the springing to the arch. */
export function forkOutline(): Ring {
  const left = leftArm();
  const right = left.map((p): Vec2 => [1 - p[0], p[1]]).reverse();
  return [...right.slice(0, -1), ...left];
}

function mullionOutline(): Ring {
  return [
    [-0.5, 0],
    [0.5, 0],
    [0.5, 1],
    [-0.5, 1],
  ];
}

const OUTLINES: Readonly<Record<TraceryKind, () => Ring>> = {
  mullion: mullionOutline,
  lightHead: lightHeadOutline,
  fork: forkOutline,
};

/* Extruded along y a unit, then turned so the outline stands in x and y and the unit runs out along z. */
function extruded(outline: Ring, style: VertexStyle): BufferGeometry {
  const builder = createMeshBuilder();
  const plan = outline.map((p): Vec2 => [p[0], p[1]]);
  prismSolid(builder, style, IDENTITY, plan, 0, 1, { top: true, bottom: true });
  return builder.build().rotateX(Math.PI / 2);
}

export function buildTraceryTemplates(palette: PaletteModel, rng: Rng): ReadonlyMap<TraceryKind, BufferGeometry> {
  const style: VertexStyle = {
    edgeId: EDGE_ID.firstObject,
    seed: rng.next(),
    strokeAngle: 0,
    extent: [0, 0],
    color: dressingStone(palette),
    surface: SURFACE.stone,
  };
  return new Map(TRACERY_KINDS.map((kind) => [kind, extruded(OUTLINES[kind](), style)]));
}
